import axios from 'axios';
// @ts-ignore
import crypto from 'crypto-browserify';
import CryptoJS from 'crypto-js';
import qs from 'qs';

import queryString from 'query-string';
import mCache from 'memory-cache';
import pkg from 'elliptic';
import { BigNumber } from 'ethers';
const { ec } = pkg;
import BN from 'bn.js';
import env from '../../env';

export type securechannel = {
  PrivateKey: string;
  Message: string;
  Encrypted: string;
  ServerPublicKey: string;
  ChannelID: string;
};

interface keyPair {
  privateKey: crypto.ECDH;
  publicKey: crypto.ECDH;
}

// Not a mandatory function. Utility function.
function getBaseURL(): string {
  return env.REACT_APP_API_SERVER;
}

const API_URL = `${env.REACT_APP_API_SERVER}/api/waas`;
const EC = new ec('p256');

export async function createSecureChannel(): Promise<securechannel> {
  /*
        Creates a secure channel using the generated public key and secure channel message.

        You can set the base URL of the WAAS API server using the `WAAS_BASE_URL` environment variable.

        Dev : https://dev-api.waas.myabcwallet.com
        Production : https://api.waas.myabcwallet.com

        Reference:
        https://docs.waas.myabcwallet.com/en/getting-started/secure-channel/

        Args:
            secure_channel_message (str): The plain text used in the request is a temporary string for channel creation verification.

        Returns:
            SecureChannel: Secure channel data.

        Raises:
            HTTPError: If the secure channel creation request fails.
  */

  try {
    const keyPair = createKeypair();
    const secureChannelMessage = 'ahnlabblockchaincompany'; //

    // Sending HTTP POST request
    const formData = qs.stringify({
      pubkey: keyPair.publicKey.getPublicKey('hex'),
      plain: secureChannelMessage,
    });

    // const urlStr = `${getBaseURL()}/secure/channel/create`;
    // const response = await axios.post(urlStr, formData, {
    //   headers: {
    //     'Content-Type': 'application/x-www-form-urlencoded',
    //   },
    // });

    const response = await axios.request({
      url: `${API_URL}/channel/create`,
      method: 'post',
      // adapter: fetchAdapter,
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
      },
      data: formData,
    });

    if (response.status !== 200) {
      throw new Error(
        `fail to create channel request, status code: ${response.status}`
      );
    }

    const serverKey = EC.keyFromPublic(response.data.publickey, 'hex');
    const sharedSecret = serverKey
      .getPublic()
      .mul(new BN(keyPair.privateKey.getPrivateKey('hex'), 16))
      .getX();
    const secretKey = sharedSecret.toString(16).padStart(64, '0'); // make sure secretKey length is 64
    mCache.put(
      'secureChannel',
      { response: response.data, keyPair, secretKey },
      20 * 60 * 1000
    );

    // Encode byte array to hexadecimal string
    const privateKeyStr = keyPair.privateKey.getPrivateKey('hex');

    const secureChannel = {
      ChannelID: response.data.channelid,
      Encrypted: response.data.encrypted,
      ServerPublicKey: response.data.publickey,
      Message: secureChannelMessage,
      PrivateKey: privateKeyStr,
      SecretKey: secretKey,
    };
    window.sessionStorage.setItem(
      'secureChannel',
      JSON.stringify(secureChannel)
    );
    return secureChannel;
  } catch (error) {
    console.error('create secure channel error:', error);
    throw error;
  }
}

function createKeypair(): keyPair {
  const ecdh = crypto.createECDH('prime256v1');
  ecdh.generateKeys();
  return {
    privateKey: ecdh,
    publicKey: ecdh,
  };
}

function verifySecureChannel(secureChannel: securechannel): boolean {
  const decryptedMessage = decrypt(secureChannel, secureChannel.Encrypted);
  return secureChannel.Message === decryptedMessage;
}

export function encrypt(secureChannel: securechannel, message: string): string {
  const { block, iv } = getAESCipher(
    secureChannel.PrivateKey,
    secureChannel.ServerPublicKey
  );

  const messageWordArray: CryptoJS.lib.WordArray =
    CryptoJS.enc.Utf8.parse(message);

  const encMsg = CryptoJS.AES.encrypt(
    messageWordArray,
    CryptoJS.enc.Hex.parse(block.toString('hex')),
    {
      iv: CryptoJS.enc.Hex.parse(iv.toString('hex')),
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7, // Using Pkcs7 padding
    }
  );

  return encMsg.toString();
}

function decrypt(
  secureChannel: securechannel,
  encryptedMessage: string
): string {
  const { block, iv } = getAESCipher(
    secureChannel.PrivateKey,
    secureChannel.ServerPublicKey
  );
  const decypteKey: CryptoJS.lib.WordArray = CryptoJS.enc.Hex.parse(
    block.toString('hex')
  );

  const copt: object = {
    iv: CryptoJS.enc.Hex.parse(iv.toString('hex')),
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  };
  const encMsg = CryptoJS.AES.decrypt(encryptedMessage, decypteKey, copt);

  const decryptedMsg = encMsg.toString(CryptoJS.enc.Utf8);
  return decryptedMsg;
}

function getAESCipher(
  privateKeyStr: string,
  publicKeyStr: string
): { block: Buffer; iv: Buffer } {
  const privateKeyBytes = Buffer.from(privateKeyStr, 'hex');
  const publicKeyBytes = Buffer.from(publicKeyStr, 'hex');

  const ecdh = crypto.createECDH('prime256v1');
  ecdh.setPrivateKey(privateKeyBytes);
  const sharedSecret = ecdh.computeSecret(publicKeyBytes);

  const key = sharedSecret.slice(0, 16);
  const iv = sharedSecret.slice(16, 32);

  return { block: key, iv: iv };
}

export async function secureChannelScenario() {
  // Creating Secure Channel
  const secureChannelRes: securechannel = await createSecureChannel();
  console.log(`Secure Channel created: ${secureChannelRes}`);

  // Verifying Secure Channel
  const verifyResult: boolean = verifySecureChannel(secureChannelRes);
  console.log(`Secure Channel Verify Result: ${verifyResult}`); // expected to be true

  // Encrypting and decrypting message using Secure Channel
  const message = 'hello, waas';
  const encryptedMessage: string = encrypt(secureChannelRes, message);
  const decryptedMessage: string = decrypt(secureChannelRes, encryptedMessage);

  console.log(`message encrypt result: ${message === decryptedMessage}`); // expected to be true
}
